import NoResultsFoundSVG from "@assets/images/no-results-found-gray.svg";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  makeStyles,
  MenuItem,
  Radio,
  Select,
  TableBody,
  TableCell,
  TableRow,
  TextField
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import ResponsiveTable from "@saleor/components/ResponsiveTable/ResponsiveTable";
import TableHead from "@saleor/components/TableHead";
import { COUPON_COLLECTION_TYPE_CHOICES } from "@saleor/coupons/constants";
import useSearchQuery from "@saleor/hooks/useSearchQuery";
import { buttonMessages } from "@saleor/intl";
import { SearchCouponCollection_search_edges_node } from "@saleor/searches/types/SearchCouponCollection";
import useScrollableDialogStyle from "@saleor/styles/useScrollableDialogStyle";
import React from "react";
import InfiniteScroll from "react-infinite-scroller";
import { FormattedMessage, useIntl } from "react-intl";

type CouponCollectionTypeFilter = "ALL" | "COMMUNITY" | "CAMPAIGN";

const useStyles = makeStyles(
  theme => ({
    actionButton: {
      "& .MuiButton-label": {
        color: "#FFFFFF"
      },
      "&:disabled": {
        "& .MuiButton-label": {
          color: "#FFFFFF"
        },
        backgroundColor: "#1848FE",
        color: "#FFFFFF",
        opacity: 0.25
      },
      backgroundColor: "#1848FE",
      borderRadius: 6,
      color: "#FFFFFF",
      fontSize: 14,
      fontWeight: 700,
      lineHeight: "20px",
      minWidth: 75,
      padding: "8px 12px",
      textTransform: "uppercase"
    },
    cancelButton: {
      "& .MuiButton-label": {
        color: "#9098A1"
      },
      border: "2px solid #9098A1",
      borderRadius: 6,
      fontSize: 14,
      fontWeight: 700,
      lineHeight: "20px",
      minWidth: 76,
      padding: "6px 10px",
      textTransform: "uppercase"
    },
    controlsRow: {
      display: "grid",
      gridColumnGap: theme.spacing(2),
      gridTemplateColumns: "1fr 220px"
    },
    dialogActions: {
      borderTop: "none",
      gap: 6,
      padding: "12px 24px 20px"
    },
    dialogContent: {
      display: "flex",
      flex: 1,
      flexDirection: "column",
      overflow: "hidden",
      padding: "0 24px"
    },
    dialogPaper: {
      borderRadius: 8,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      maxWidth: 673
    },
    closeButton: {
      color: "#393636",
      padding: 4,
      position: "absolute",
      right: 16,
      top: 16
    },
    dialogTitle: {
      "& h2": {
        color: "#393636",
        fontSize: 32 / 1.6, // 20px
        fontWeight: 600
      },
      borderBottom: "none",
      padding: "20px 24px 18px",
      position: "relative"
    },
    headerCell: {
      "&.MuiTableCell-head": {
        paddingLeft: 2
      },
      color: "#393636",
      fontSize: 16,
      fontWeight: 600,
      textAlign: "left"
    },
    listContainer: {
      display: "flex",
      flex: 1,
      flexDirection: "column",
      marginTop: 14,
      minHeight: 0,
      overflow: "hidden"
    },
    listBodyContainer: {
      flex: 1,
      maxHeight: "none",
      minHeight: 0,
      overflowY: "auto"
    },
    nameCell: {
      width: "40%"
    },
    menuItem: {
      color: "#494E56",
      fontSize: 18,
      minHeight: 40
    },
    menuItemSelected: {
      "&.Mui-selected": {
        backgroundColor: "#F4F8FF",
        color: "#0030FF",
        fontWeight: 500
      }
    },
    noResultsCell: {
      borderBottom: "none",
      padding: "24px 0 32px",
      textAlign: "center"
    },
    noResultsIcon: {
      height: 64,
      marginBottom: 8,
      width: 64
    },
    noResultsSubtitle: {
      color: "#747C8C",
      fontSize: 14,
      fontWeight: 400,
      lineHeight: "20px"
    },
    noResultsTitle: {
      color: "#747C8C",
      fontSize: 16,
      fontWeight: 500,
      lineHeight: "24px",
      marginBottom: 2
    },
    noResultsWrap: {
      alignItems: "center",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      width: "100%"
    },
    radioRoot: {
      "&.Mui-checked": {
        color: "#1848FE"
      },
      color: "#1848FE",
      padding: 0
    },
    radioTableCell: {
      "&.MuiTableCell-paddingCheckbox": {
        paddingLeft: 0,
        paddingRight: 12
      },
      borderBottom: "none",
      paddingRight: 12,
      textAlign: "right",
      width: 48
    },
    row: {
      height: 42
    },
    rowCell: {
      borderBottom: "none",
      color: "#393636",
      fontSize: 16,
      paddingBottom: 10,
      paddingLeft: 2,
      paddingTop: 10
    },
    searchInput: {
      marginTop: 0,
      "& .MuiInputBase-input": {
        color: "#494E56",
        fontSize: 12,
        padding: "10px 14px"
      },
      "& .MuiInputBase-input:-ms-input-placeholder": {
        color: "#A1A1A1 !important",
        opacity: "1 !important"
      },
      "& .MuiInputBase-input::-ms-input-placeholder": {
        color: "#A1A1A1 !important",
        opacity: "1 !important"
      },
      "& .MuiInputBase-input::-moz-placeholder": {
        color: "#A1A1A1 !important",
        opacity: "1 !important"
      },
      "& .MuiInputBase-input::-webkit-input-placeholder": {
        color: "#A1A1A1 !important",
        opacity: "1 !important"
      },
      "& .MuiInputBase-input::placeholder": {
        color: "#A1A1A1 !important",
        opacity: "1 !important"
      },
      "& .MuiInputBase-input:focus::placeholder": {
        color: "#A1A1A1 !important",
        opacity: "1 !important"
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "#E5E5E5"
      },
      "& .MuiOutlinedInput-root": {
        borderRadius: 7,
        minHeight: 40
      }
    },
    selectLabel: {
      display: "none"
    },
    selectRoot: {
      "& .MuiOutlinedInput-input": {
        color: "#000000",
        fontSize: 16,
        padding: "10px 14px"
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "#E5E5E5"
      },
      "& .MuiOutlinedInput-root": {
        borderRadius: 7,
        minHeight: 40
      }
    },
    tableHeadRow: {
      borderBottom: "1px solid #E8EAEE"
    },
    tableScrollable: {
      "& .MuiTableCell-root:first-child:not(.MuiTableCell-paddingCheckbox)": {
        paddingLeft: 2
      },
      marginTop: 0,
      tableLayout: "fixed"
    },
    tableStatic: {
      "& .MuiTableCell-root:first-child:not(.MuiTableCell-paddingCheckbox)": {
        paddingLeft: 2
      },
      marginBottom: 0,
      tableLayout: "fixed"
    },
    typeCell: {
      "&.MuiTableCell-root": {
        paddingLeft: 2
      },
      width: "calc(60% - 48px)"
    },
    typeSelect: {
      minWidth: 220
    }
  }),
  { name: "AssignCouponCollectionDialog" }
);

export interface AssignCouponCollectionDialogProps {
  open?: boolean;
  loading?: boolean;
  hasMore?: boolean;
  couponCollections?: SearchCouponCollection_search_edges_node[];
  onClose?: () => void;
  onFetch?: (query: string) => void;
  onFetchMore?: () => void;
  onSubmit?: (id: string) => void;
}

function normalizeType(
  couponCollection: SearchCouponCollection_search_edges_node
): CouponCollectionTypeFilter | null {
  const value = couponCollection.collectionType || "";
  if (!value) {
    return null;
  }
  if (value.toUpperCase() === "COMMUNITY") {
    return "COMMUNITY";
  }
  if (value.toUpperCase() === "CAMPAIGN") {
    return "CAMPAIGN";
  }
  return null;
}

function getTypeLabel(
  couponCollection: SearchCouponCollection_search_edges_node
): string {
  const normalizedType = normalizeType(couponCollection);
  if (!normalizedType || normalizedType === "ALL") {
    return "—";
  }
  const choice = COUPON_COLLECTION_TYPE_CHOICES.find(
    item => item.value === normalizedType
  );
  return choice ? choice.label : normalizedType;
}

const AssignCouponCollectionDialog: React.FC<AssignCouponCollectionDialogProps> = ({
  open,
  loading,
  hasMore,
  couponCollections = [],
  onClose,
  onFetch,
  onFetchMore,
  onSubmit
}) => {
  const intl = useIntl();
  const classes = useStyles({});
  const scrollableDialogClasses = useScrollableDialogStyle({});
  const [selectedCollectionId, setSelectedCollectionId] = React.useState("");
  const [query, onQueryChange, resetQuery] = useSearchQuery(onFetch);
  const [typeFilter, setTypeFilter] = React.useState<
    CouponCollectionTypeFilter
  >("ALL");

  const filteredCollections = React.useMemo(
    () =>
      couponCollections.filter(collection => {
        const matchesType =
          typeFilter === "ALL" ||
          (collection.collectionType ?? "").toUpperCase() === typeFilter;
        return matchesType;
      }),
    [couponCollections, typeFilter]
  );

  const isSelectedCollectionVisible = filteredCollections.some(
    collection => collection.id === selectedCollectionId
  );

  React.useEffect(() => {
    if (!open) {
      setSelectedCollectionId("");
      resetQuery?.();
      setTypeFilter("ALL");
    }
  }, [open]);

  React.useEffect(() => {
    if (selectedCollectionId && !isSelectedCollectionVisible) {
      setSelectedCollectionId("");
    }
  }, [selectedCollectionId, isSelectedCollectionVisible]);

  React.useEffect(() => {
    if (
      typeFilter !== "ALL" &&
      filteredCollections.length < 10 &&
      hasMore &&
      !loading
    ) {
      onFetchMore?.();
    }
  }, [filteredCollections.length, typeFilter, hasMore, loading, onFetchMore]);

  const handleAssign = () => {
    if (selectedCollectionId && isSelectedCollectionVisible) {
      onSubmit?.(selectedCollectionId);
    }
  };

  return (
    <Dialog
      open={Boolean(open)}
      onClose={onClose}
      classes={{ paper: scrollableDialogClasses.dialog }}
      PaperProps={{ className: classes.dialogPaper }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle className={classes.dialogTitle}>
        <FormattedMessage
          defaultMessage="Coupon Collection"
          description="dialog title"
        />
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <div className={classes.controlsRow}>
          <TextField
            className={classes.searchInput}
            placeholder={intl.formatMessage({
              defaultMessage: "Search a Coupon Collection"
            })}
            fullWidth
            value={query}
            onChange={onQueryChange}
            variant="outlined"
            InputProps={{
              autoComplete: "off",
              endAdornment: loading && <CircularProgress size={16} />
            }}
          />
          <FormControl
            className={`${classes.typeSelect} ${classes.selectRoot}`}
          >
            <InputLabel className={classes.selectLabel}>
              <FormattedMessage
                defaultMessage="Type"
                description="filter label"
              />
            </InputLabel>
            <Select
              value={typeFilter}
              onChange={event =>
                setTypeFilter(event.target.value as CouponCollectionTypeFilter)
              }
              variant="outlined"
            >
              <MenuItem value="ALL">
                <FormattedMessage
                  defaultMessage="All Types"
                  description="all type option"
                />
              </MenuItem>
              {COUPON_COLLECTION_TYPE_CHOICES.map(choice => (
                <MenuItem
                  key={choice.value}
                  value={choice.value}
                  className={`${classes.menuItem} ${classes.menuItemSelected}`}
                >
                  {choice.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        <div className={classes.listContainer}>
          <ResponsiveTable className={classes.tableStatic}>
            <colgroup>
              <col className={classes.nameCell} />
              <col className={classes.typeCell} />
              <col className={classes.radioTableCell} />
            </colgroup>
            <TableHead
              colSpan={3}
              disabled={loading}
              hideCheckBox
              items={couponCollections}
              className={classes.tableHeadRow}
            >
              <TableCell className={classes.headerCell}>
                <FormattedMessage defaultMessage="Coupon Collection Name" />
              </TableCell>
              <TableCell
                className={`${classes.headerCell} ${classes.typeCell}`}
              >
                <FormattedMessage defaultMessage="Type" />
              </TableCell>
              <TableCell className={classes.radioTableCell} />
            </TableHead>
          </ResponsiveTable>
          <div className={classes.listBodyContainer}>
            <InfiniteScroll
              pageStart={0}
              loadMore={onFetchMore}
              hasMore={hasMore}
              useWindow={false}
              loader={
                <div
                  className={scrollableDialogClasses.loadMoreLoaderContainer}
                >
                  <CircularProgress size={16} />
                </div>
              }
              threshold={10}
            >
              <ResponsiveTable className={classes.tableScrollable}>
                <colgroup>
                  <col className={classes.nameCell} />
                  <col className={classes.typeCell} />
                  <col className={classes.radioTableCell} />
                </colgroup>
                <TableBody>
                  {filteredCollections.length > 0
                    ? filteredCollections.map(collection => (
                        <TableRow
                          key={collection.id}
                          hover
                          className={classes.row}
                        >
                          <TableCell className={classes.rowCell}>
                            {collection.title}
                          </TableCell>
                          <TableCell
                            className={`${classes.rowCell} ${classes.typeCell}`}
                          >
                            {getTypeLabel(collection)}
                          </TableCell>
                          <TableCell
                            className={classes.radioTableCell}
                            padding="checkbox"
                          >
                            <Radio
                              className={classes.radioRoot}
                              checked={selectedCollectionId === collection.id}
                              onChange={() =>
                                setSelectedCollectionId(collection.id)
                              }
                              value={collection.id}
                              color="primary"
                              name="couponCollectionSelection"
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    : !loading && (
                        <TableRow>
                          <TableCell
                            colSpan={3}
                            className={classes.noResultsCell}
                          >
                            <div className={classes.noResultsWrap}>
                              <img
                                src={NoResultsFoundSVG}
                                alt=""
                                className={classes.noResultsIcon}
                              />
                              <div className={classes.noResultsTitle}>
                                <FormattedMessage defaultMessage="No results found" />
                              </div>
                              <div className={classes.noResultsSubtitle}>
                                <FormattedMessage defaultMessage="Please check your keyword and try again." />
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                </TableBody>
              </ResponsiveTable>
            </InfiniteScroll>
          </div>
        </div>
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Button className={classes.cancelButton} onClick={onClose}>
          {intl.formatMessage(buttonMessages.cancel)}
        </Button>
        <Button
          className={classes.actionButton}
          variant="contained"
          onClick={handleAssign}
          disabled={loading || !isSelectedCollectionVisible}
        >
          <FormattedMessage
            defaultMessage="Assign"
            description="assign button"
          />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AssignCouponCollectionDialog.displayName = "AssignCouponCollectionDialog";
export default AssignCouponCollectionDialog;
